import { GroupByTypeEnum } from "app/containers/Features/containers/Boards/types";
import {
  ENUM_USER_TIME_FORMAT,
  ENUM_USER_WEEK_START,
  IAvatar,
} from "app/containers/Features/containers/TeamSettings/container/MySetting/types";
import { IMember } from "app/containers/Features/containers/TeamSettings/types";
import { PLAN_TYPE } from "app/containers/OnBoarding/types";
import { ITags } from "./containers/Features/components/CommonTagSelection/types";
import { ITicketTypes } from "./containers/Features/containers/TeamSettings/container/Projects/container/WorkItems/types";

export interface FormElement {
  value: any;
  error: string;
}

export interface FormError {
  name: string;
  error: string;
}

export interface AuthorizedAccount {
  email: string;
  password: string;
}

export type Project = {
  id: string;
  name: string;
  icon: {
    avatar: string;
    color: string;
    backgroundColor: string;
  };
  owner: Owner;
  plan: PLAN_TYPE;
  statuses: Statuses[];
  members: TeamMember[];
  createdAt: string;
  storageUsage: number;
};

export enum Statuses {
  TODO = "TO DO",
  IN_PROGRESS = "IN PROGRESS",
  DONE = "DONE",
  REOPENED = "REOPENED",
  IN_CODE_REVIEW = "IN CODE REVIEW",
  ABANDONED = "ABANDONED",
  IN_STAGING = "IN STAGING",
  TESTING_IN_PRODUCTION = "TESTING IN PRODUCTION",
}

export type Owner = {
  id: string;
  name: string;
  avatar: string;
  isOnline: boolean;
};

export enum ActiveStatus {
  online = "online",
  offline = "offline",
  away = "away",
  break = "break",
}

export interface User {
  _id: string;
  email: string;
  fullName?: string;
  companySize?: string;
  referalSource?: string;
  toolPreferences?: string[];
  team?: Team;
  initials: string;
  role: string;
  status: string;
  lastLogin: string;
  localTime: string;
  location: string;
  spokenLanguages: string[];
  creationDate: string;
  mobileNumber: string;
  members: TeamMember[];
}

export interface Team {
  name: string;
  members: TeamMember[];
  plan: PLAN_TYPE;
}

export interface TeamMember {
  status?: string;
  email?: string;
  localTime?: string;
  name?: string;
  role?: string;
  id?: string;
  team?: string;
  lastLogin?: string;
  location?: string;
  spokenLanguages?: string;
  mobileNumber?: string;
  creationDate?: string;
}

export interface Option {
  label: string;
  value: string;
  description?: string;
  icon?: React.ReactNode;
  searchText?: string;
  tick?: string;
  role?: string;
  disabled?: boolean;
}

export type TeamMemberType = {
  name: FormElement;
  role: FormElement;
};

export enum userOnboardingStage {
  CODE_VERIFICATION = "CODE_VERIFICATION",
  CREATE_PROFILE = "CREATE_PROFILE",
  ON_BOARDING_INFO = "ON_BOARDING_INFO",
  CREATE_TEAM = "CREATE_TEAM",
  CREATE_PROJECT = "CREATE_PROJECT",
  PROJECT_MEMBER_CREATION = "PROJECT_MEMBER_CREATION",
  PLAN_SELECTION = "PLAN_SELECTION",
  COMPLETED = "COMPLETED",
}
export enum TeamRole {
  NONE = "None",
  TEAM_OWNER = "Team Owner",
  ADMINISTRATOR = "Administrator",
  PROJECT_MANAGER = "Project Manager",
  SCRUM_MASTER = "Scrum Master",
  PRODUCT_OWNER = "Product Owner",
  DEVELOPER = "Developer",
  QA_TESTER = "QA Tester",
  DESIGNER = "Designer",
  TEAM_MEMBER = "Team Member",
  GUEST_USER = "Guest User",
  ANALYST = "Analyst",
  CUSTOMER_SUCCESS_MANAGER = "Customer Success Manager",
  STAKEHOLDER = "Stakeholder",
}
export interface IUserData {
  _id: string;
  createdAt: string;
  updatedAt: string;
  fullName: string;
  email: string;
  isActive: boolean;
  avatar: IAvatar;
  userOnboardingProgress: userOnboardingStage;
  language: string;
  timeFormat: ENUM_USER_TIME_FORMAT.TWELVE_HOUR;
  dateFormat: ENUM_USER_DATE_FORMAT.MM_DD_YYYY;
  weekStart: ENUM_USER_WEEK_START.SUNDAY;
  isInvited: boolean;
  timeZone: string;
  lastLoginDate: string;
  selectedProjectId: string;
  selectedTeamId: string;
  mobileNumber: string;
  mutedTill: string | null;
}

type ISODateString = Date;

export interface IStorageUsage {
  current: number;
  total: number;
}
export enum TeamPlanType {
  FREE = "free",
  BUSINESS = "business",
  PROFESSIONAL = "professional",
}

export enum TeamStatus {
  ACTIVE = "active",
  TODO = "TO DO",
  IN_PROGRESS = "IN PROGRESS",
  DONE = "DONE",
  REOPENED = "REOPENED",
  IN_CODE_REVIEW = "IN CODE REVIEW",
  ABANDONED = "ABANDONED",
  IN_STAGING = "IN STAGING",
  TESTING_IN_PRODUCTION = "TESTING IN PRODUCTION",
}

export enum ProjectStatus {
  ACTIVE = "active",
  IN_ACTIVE = "inactive",
}

export enum MemberStatus {
  ACTIVE = "Active",
  INVITATION_PENDING = "Invitation pending",
  INVITATION_REJECTED = "Invitation rejected",
}

export interface IIcon {
  initials: string;
  color: string;
  bgColor: string;
}

export interface IProjectProgress {
  current: number;
  total: number;
}
export enum BoardType {
  Backlog = "Backlog",
  QADashboard = "QA Dashboard",
  DevBoard = "Dev Board",
  Custom = "Custom",
  HOME = "Home",
}
export enum BoardViewType {
  KANBAN = "Kanban",
  LIST = "List",
}
export interface IOwner {
  _id: string;
  email: string;
}
export interface ICustomizeColumns {
  _id: string;
  createdAt: string;
  updatedAt: string;
  fieldName: string;
  order: number;
  disabled: boolean;
  isVisible: boolean;
  type: GroupByTypeEnum;
  width: number;
  min: number;
  max: number;
}

export enum BoardPermission {
  PRIVATE = "Private",
  PUBLIC = "Public",
  Custom = "Custom",
}
export interface IBoardSetting {
  viewType: BoardViewType;
  Type: BoardType;
  permission?: BoardPermission;
  groupBy?: GroupByTypeEnum;
  groups?: any[];
  favorite?: number;
  customizeColumns?: ICustomizeColumns[];
}

export interface IBoard {
  _id: string;
  name: string;
  order: number;
  owner?: IOwner;
  settings: IBoardSetting;
}

export interface IWorkspaceTeams {
  _id: string;
  teamName: string;
  avatar: IAvatar;
  projects: IWorkspaceProjects[];
  teamMembers: string[];
}
export interface IWorkspaceProjects {
  _id: string;
  projectName: string;
  status: ProjectStatus;
  avatar: IAvatar;
  tags: ITagsList[];
  ticketIds: string[];
  isHiddenInSidebar: boolean;
}
export interface IWorkSpaceDetails {
  _id: string;
  teams: IWorkspaceTeams[];
  teamIds: string[];
}
export enum sortOrder {
  ASC = "asc",
  DESC = "desc",
}
export enum BoardTemplateEnum {
  LIST = "List",
  KANBAN = "Kanban",
  AGILE_DEVELOPMENT = "Agile Development",
  BUG_TRACKING = "Bug Tracking",
  PRODUCT_ROADMAP = "Product Roadmap",
  RELEASE_MANAGEMENT = "Release Management",
  DESIGN_AND_PROTOTYPING = "Design and Prototyping",
  GENERAL_PROJECT_MANAGEMENT = "General Project Management",
}
export interface IBoardFixedTemp {
  templateType: BoardTemplateEnum;
  projectId: string;
  teamId: string;
  visibleBoard: number;
}
export interface ITicketStatuses {
  _id: string;
  name: string;
  color: string;
  permissions: string[];
  ticketTypes: string[];
  updatedAt: string;
  createdAt: string;
}

export interface ITicketStatus {
  _id: string;
  name: string;
  color: string;
}
export interface IGroups {
  _id: string;
  statusId: string;
  name: string;
}
export interface IVisibleGroup {
  _id: string;
  statusId: string;
  title: string;
  color: string;
  isVisible: boolean;
  order: number;
}
export interface IGroupBy {
  title: string;
  value: GroupByTypeEnum;
}

export interface ISubGroupByData {
  title: string;
  value: GroupByTypeEnum;
}
export interface ICustomizeCardsData {
  fieldName: string;
  order: number;
  disabled: boolean;
  isVisible: boolean;
  type: GroupByTypeEnum;
  createdAt: string;
  updatedAt: string;
  _id: string;
}
export interface IRawSettings {
  groups: IGroups[];
  visibleGroups: IVisibleGroup[];
  subGroups: [];
  groupByData: IGroupBy[];
  subGroupByData: ISubGroupByData[];
  customizeCardsData: ICustomizeCardsData[];
}
export interface ITeamSettings {
  _id: string;
  teamName: string;
  teamMembers: string[];
  projects: IWorkspaceProjects[];
  ticketTypes: ITicketTypes[];
  ticketStatus: ITicketStatus[];
  rawSettings: IRawSettings;
}

export interface ITicketList {
  _id: string;
  type: {
    _id: string;
    name: string;
    icon: string;
  };
  status: {
    _id: string;
    name: string;
    color: string;
  };
  ticketID: string;
  ticketName: string;
  assignedTo: {
    _id: string;
    name: string;
    email: string;
  }[];
  dueDate: string;
  priority: string;
  effortEstimated: [];
  effortActual: [];
  tags: ITagsList[];
  longDescription: string;
  checklists: [];
  attachments: [];
  customFields: [];
  release: string;
  sprint: string;
  epic: string;
  targetRelease: string;
  environment: string;
  severity: string;
  component: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdDate: string;
  groupId: string;
}

export interface ITagsList {
  color: string;
  createdAt: string;
  createdBy: string;
  isArchived: boolean;
  title: string;
  updatedAt: string;
  _id: string;
}
export interface IUpdateBoardOrder {
  _id: string;
  order: number;
}
export interface IBoardMenu {
  _id: string;
  boardId: string;
  name: string;
  order: number;
  Type: BoardType;
  viewType: BoardViewType;
  favorite: number;
}
export enum ArchiveType {
  PROJECT = "project",
  TICKET = "ticket",
  BOARD = "board",
  CUSTOM_FIELD = "customField",
  TICKET_TYPE = "ticketType",
  WORK_FLOW = "workFlow",
  TRANSITION = "transition",
  TICKET_STATUS = "ticketStatus",
  TEAM_MEMBER = "teamMember",
  TICKET_TAG = "ticketTag",
  TRANSITION_RULE = "transitions rule",
  COMPONENT = "component",
}

export interface IPagination {
  search?: string;
  availableSearch?: string[];
  page: number;
  perPage: number;
  orderBy: string;
  orderDirection: sortOrder;
  availableOrderBy?: string[];
  availableOrderDirection?: string[];
  total: number;
  totalPage: number;
}

export interface IMembers {
  _id: string;
  name: string;
  email: string;
  role: TeamRole;
  avatar: IAvatar;
}
export interface IProjectSettings {
  _id: string;
  name: string;
  lead: string;
  status: string;
  isArchived: boolean;
  ticketTypes: ITicketTypes[];
  ticketStatuses: ITicketStatuses[];
  ticketTags: ITags[];
}

export enum ERROR_TYPE {
  DEFAULT = "DEFAULT",
  IMPORT = "IMPORT",
  NAME_EXIST = "NAME_EXIST",
  EMAIL_EXIST = "EMAIL_EXIST",
  PASSWORD_MISMATCH = "PASSWORD_MISMATCH",
  PASSWORD_NOT_SET_YET = "PASSWORD_NOT_SET_YET",
  INVALID_INPUT = "INVALID_INPUT",
  USER_ALREADY_EXISTS = "USER_ALREADY_EXISTS",
  USER_ALREADY_VERIFIED = "USER_ALREADY_VERIFIED",
  INVALID_OTP = "INVALID_OTP",
  VERIFICATION_CODE_EXPIRED = "VERIFICATION_CODE_EXPIRED",
  INVALID_VERIFICATION_ID = "INVALID_VERIFICATION_ID",
  TOKEN_EXPIRED = "TOKEN_EXPIRED",
  INVALID_TOKEN = "INVALID_TOKEN",
  INVALID_ID = "INVALID_ID",
  USER_BLOCKED = "USER_BLOCKED",
  INACTIVE_PERMANENT = "INACTIVE_PERMANENT",
  INACTIVE = "INACTIVE",
  PASSWORD_EXPIRED = "PASSWORD_EXPIRED",
  PASSWORD_ATTEMPT_MAX = "PASSWORD_ATTEMPT_MAX",
  OLD_PASSWORD_MISSING = "OLD_PASSWORD_MISSING",
  PASSWORD_LINK_EXPIRED = "PASSWORD_LINK_EXPIRED",
}
export interface AppHomeState {
  userFormData: IUserData; // user initial data
  isAuthenticated: boolean;
  authorizedAccounts: AuthorizedAccount[];
  loading: boolean;
  error: string | null;
  drawerOpen: { actions: boolean; id?: string }; // user profile
  project: Project | null;
  userOnboardingProgress: userOnboardingStage | null;
  access_token: string | null;

  workSpaceDetails: IWorkSpaceDetails;
  selectedTeam: IWorkspaceTeams | null;
  workspaceTeamsSetting: ITeamSettings[];
  // selectedProjectSetting: IWorkspaceProjects;
  isBoardCreate: boolean;
  isBoardLoading: boolean;
  boards: IUpdateBoardOrder[];
  isBoardMenuLoading: boolean;
  boardMenuData: IBoardMenu[];
  isArchiveLoading: boolean;
  isCollapseSidebarMenu: boolean; // side bar hide and show
  createTeamModal: boolean; // team create modal show
  createProjectModal: boolean; // project create modal show
  selectedTeamLoading: boolean;
  projectSettingDetails: IProjectSettings;
  globalSearchModal: boolean;
  notificationDrawerOpen: boolean;
  isTimeSheetEnabled: boolean;

  // Timer State Type
  timeTracker: {
    startTime: number | null;
    elapsed: number;
    isRunning: boolean;
  };
}

export enum ENUM_USER_DATE_FORMAT {
  MM_DD_YYYY = "mm/dd/yyyy",
  DD_MM_YYYY = "dd/mm/yyyy",
  YYYY_MM_DD = "yyyy/mm/dd",
}

export interface IMetaData {
  languages: string[];
  timestamp: number;
  timezone: string;
  requestId: string;
  path: string;
  version: string;
  repoVersion: string;
  pagination: IPagination;
}

export interface ISeverity {
  id: string;
  color: string;
}

export enum PriorityFilterEnum {
  NONE = "None",
  LOW = "Low",
  NORMAL = "Normal",
  HIGH = "High",
  URGENT = "Urgent",
}
export enum StatusFilterEnum {
  OPENED = "Opened",
  IN_PROCESS = "In Process",
  DUPLICATE = "Duplicate",
  CLOSED = "Closed",
  TODO = "To Do",
}
export enum TicketSeverityEnum {
  MODERATE = "Moderate",
  MINOR = "Minor",
  MAJOR = "Major",
  CRITICAL = "Critical",
}

export enum WatcherType {
  CREATOR = "creator",
  ASSIGNEE = "assignee",
  WATCHER = "watcher",
}

export type TotalEffortType = {
  hours: number;
  minutes: number;
};
